import Base from "./_BaseComponent";
import $ from "jquery";

/*
const STORES_ENDPOINT =
	"/wp-content/themes/tilling-web-2020-wp/assets/json/stores.json";

const POSTCODE_ENDPOINT =
	"/wp-content/themes/tilling-web-2020-wp/assets/json/suburbs.json";
*/

const MAX_DISTANCE = 100;
const R = 6371;

type SuburbEntry = {
	Lat: number;
	Lon: number;
	Postcode: string;
	State: string;
	Suburb: string;
};

type StoreListing = {
	Address: string;
	Hours: string;
	IsArchitectural: boolean;
	IsEngineered: boolean;
	Lat: number;
	Lon: number;
	Name: string;
	Postcode: string;
	Phone: string;
	Suburb: string;
	Distance: number;
};

class Storelocator extends Base.Component {
	private storeListEl: JQuery<HTMLElement>;
	private searchEl: JQuery<HTMLElement>;
	private storeList: Array<any>;
	private suburbMap: any;
	private didLoad: boolean = false;
	private showEngineeredOnly: boolean = false;
	private btnFilterEl: JQuery<HTMLElement>;
	private btnClearFilterEl: JQuery<HTMLElement>;
	private lastStoreList: Array<StoreListing> = [];
	private storeEndpoint: string;
	private postcodeEndpoint: string;

	public onMount(): void {
		this.storeListEl = this.element.find("[data-store-list]").first();
		this.searchEl = this.element.find("[data-triggers-search]").first();
		this.btnFilterEl = this.element.find("[data-toggle-filter]").first();
		this.btnClearFilterEl = this.element
			.find("[data-clear-filter]")
			.first();

		this.searchEl.on("change keyup", (e: Event) => {
			this.doPostcodeSearch($(e.currentTarget).val() as string);
		});

		this.btnFilterEl.on("click", (e: Event) => {
			e.preventDefault();
			this.applyEngineerFilter();
		});

		this.btnClearFilterEl.on("click", (e: Event) => {
			e.preventDefault();
			this.clearEngineerFilter();
		});

		if ((window as any).hasOwnProperty("resellerEndpoints")) {
			if ((window as any).resellerEndpoints.hasOwnProperty("stores"))
				this.storeEndpoint = (window as any).resellerEndpoints.stores;

			if ((window as any).resellerEndpoints.hasOwnProperty("postcodes"))
				this.postcodeEndpoint = (window as any).resellerEndpoints.postcodes;

			this.setupData();
		}

	}

	private applyEngineerFilter(): void {
		this.btnFilterEl.addClass("active");
		this.btnClearFilterEl.removeClass("active");
		this.showEngineeredOnly = true;
		this.render(this.lastStoreList);
	}

	private clearEngineerFilter(): void {
		this.btnFilterEl.removeClass("active");
		this.btnClearFilterEl.addClass("active");
		this.showEngineeredOnly = false;
		this.render(this.lastStoreList);
	}

	private async setupData(): Promise<void> {
		await this.initData();
		this.defaultFillStores();

		if (navigator.geolocation)
			this.tryGeo();
	}

	private tryGeo() {
		navigator.geolocation.getCurrentPosition((position) => {

			// Find closest post code.
			let postcode = null;
			let best = 99999999999999;

			for (const k in this.suburbMap) {
				const distance = this.getDistanceFromLatLonInKm(
					position.coords.latitude,
					position.coords.longitude,
					Number(this.suburbMap[k].Lat),
					Number(this.suburbMap[k].Lon)
				);

				if (distance < best) {
					best = distance;
					postcode = this.suburbMap[k].Postcode
				};
			}

			if (postcode) {
				this.searchEl.val(postcode);
				this.searchEl.trigger("keyup");
			}

		});
	}

	private defaultFillStores(): void {
		const results = [];
		for (let store of this.storeList) {
			const distance = 0;
			results.push(this.convertRawStoreToStoreListing(store, distance));
		}

		this.render(results);
	}

	private async initData(): Promise<void> {
		try {
			this.storeList = await this.fetch(this.storeEndpoint);

			this.storeList.sort((a, b) => {
				return a.n.localeCompare(b.n);
			});

			this.suburbMap = await this.fetch(this.postcodeEndpoint);
			this.didLoad = true;
		} catch (e) {
			console.error(e);
		}
	}

	private doPostcodeSearch(postcode: string): void {
		if (!this.didLoad) return;

		try {
			const currentSuburb = this.getSuburbByPostcode(postcode);
			const stores = this.getCloseStores(currentSuburb);
			this.render(stores);
		} catch (e) {
			if (postcode.length == 0) this.defaultFillStores();
			else this.clearStores();
		}
	}

	private clearStores(): void {
		this.storeListEl.html("");
	}

	private render(stores: Array<StoreListing>): void {
		let buffer = "";

		for (let store of stores) {
			if (this.showEngineeredOnly && store.IsEngineered == false)
				continue;

			let distanceString = "";
			if (store.Distance > 0)
				distanceString = `Approx. ${store.Distance.toFixed(2)}KM away`;

			buffer += `<div class="entry">
						<div class="title ${distanceString ? "with-distance" : ""}">${store.Name}</div>
						${distanceString ? `<div class="distance">${distanceString}</div>` : ''}
						<div class="address">${store.Address}, ${store.Suburb} ${store.Postcode}</div>
						<div class="contact">${store.Phone}</div>
						<div class="action">
							<a href="https://maps.google.com.au/?q=${[store.Address, store.Suburb, store.Postcode].map(encodeURIComponent).join("&")}" target="_blank" class="btn btn-primary-inverted">View on map <i class="fa fa-location-arrow" aria-hidden="true"></i></a>
						</div>
					</div>`;
		}

		this.lastStoreList = stores;
		this.storeListEl.html(buffer);
	}

	private getCloseStores(suburb: SuburbEntry): Array<StoreListing> {
		const results: Array<StoreListing> = [];

		for (let store of this.storeList) {
			const distance = this.getDistanceFromLatLonInKm(
				store.lt,
				store.ln,
				suburb.Lat,
				suburb.Lon
			);

			if (distance < MAX_DISTANCE)
				results.push(
					this.convertRawStoreToStoreListing(store, distance)
				);
		}

		results.sort((a, b) => {
			return a.Distance - b.Distance;
		});

		return results;
	}

	private convertRawStoreToStoreListing(
		store: any,
		distance: number
	): StoreListing {
		return {
			Address: store.a,
			Hours: store.h,
			IsArchitectural: store.ia ? true : false,
			IsEngineered: store.ie ? true : false,
			Lat: store.lt,
			Lon: store.ln,
			Name: store.n,
			Postcode: store.p.toString(),
			Phone: store.ph,
			Suburb: store.s,
			Distance: distance,
		};
	}

	private getSuburbByPostcode(postcode: string): SuburbEntry {
		if (postcode.length < 3) throw "Invalid postcode length";

		if (!this.suburbMap.hasOwnProperty(postcode)) throw "Suburb not found";

		return this.suburbMap[postcode] as SuburbEntry;
	}

	private async fetch(uri: string): Promise<Array<any>> {
		return await $.ajax({
			method: "GET",
			url: uri,
		});
	}

	private getDistanceFromLatLonInKm(
		lat1: number,
		lon1: number,
		lat2: number,
		lon2: number
	): number {
		var dLat = this.deg2rad(lat2 - lat1);
		var dLon = this.deg2rad(lon2 - lon1);
		var a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(this.deg2rad(lat1)) *
				Math.cos(this.deg2rad(lat2)) *
				Math.sin(dLon / 2) *
				Math.sin(dLon / 2);
		var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		var d = R * c; // Distance in km
		return d;
	}

	private deg2rad(deg: number) {
		return deg * (Math.PI / 180);
	}
}

export default Base.Init("[data-component-storelocator]", Storelocator);
